/**
 * Backend → panel shapes for the catalogue, sub-category and packing sections.
 *
 * The panel was built against a frozen sample dataset whose records carry more than the backend
 * knows (per-SKU weight histograms, merge lineage, a spread score). Those fields are omitted here
 * rather than faked: a component that shows "—" for a figure we do not have is honest, and one
 * that shows an invented number is not. `mapRequest.js` does the same job for requests.
 */

const num = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v))

/** One catalogue row. Field names match what CatalogueTable and catalogueMath already read. */
export function mapCatalogueRow(r) {
  if (!r || !r.sku) return null
  return {
    sku: r.sku,
    product: r.product || '',
    cat: r.cat || '',
    sub: r.sub || '',
    ships: num(r.ships) || 0,
    deadKg: num(r.deadKg),
    volKg: num(r.volKg),
    chargeKg: num(r.chargeKg),
    dims: [num(r.lengthCm), num(r.breadthCm), num(r.heightCm)],
    // `dq` is the panel's "this row needs attention" flag; the backend's version of that is
    // "no category, so it cannot be frozen at category level".
    dq: !!r.needsCategory,
    needsCategory: !!r.needsCategory,
    // Present in the sample dataset, genuinely unknown from the product master. Left null so the
    // table renders a dash instead of a made-up figure.
    mpw: null,
    spread: null,
    hist: [],
    packId: null,
    source: 'catalogue',
    reviewed: true,
    motherOf: null,
    childOf: null,
    sorterCount: null,
    merchantCount: null,
  }
}

export function mapCataloguePage(page) {
  const rows = ((page && page.rows) || []).map(mapCatalogueRow).filter(Boolean)
  return {
    rows,
    hasMore: !!(page && page.hasMore),
    skip: (page && page.skip) || 0,
    limit: (page && page.limit) || rows.length,
    categoryField: (page && page.categoryField) || 'category',
    note: (page && page.note) || null,
  }
}

/**
 * The panel renders sub-categories as `{category: [sub, ...]}`.
 *
 * The server sends the real hierarchy: both values sit on the same product document, so it can say
 * which sub-categories belong under which category. What it cannot place are sub-categories on SKUs
 * with no category recorded at all — those arrive as rows with no `cat`, and are collected under one
 * heading here so a merchant can still pick them, rather than being attached to a category at random.
 */
export const UNGROUPED = 'All sub-categories'

export function mapSubcategories(payload) {
  const map = (payload && payload.map) || {}
  const out = {}
  Object.keys(map).forEach((cat) => { out[cat] = (map[cat] || []).slice() })
  // Additive, not a fallback: a merchant can have a proper hierarchy AND a few unfiled SKUs, and
  // dropping the unfiled ones would quietly make those sub-categories unfreezable.
  const placed = new Set(Object.keys(out).reduce((all, cat) => all.concat(out[cat] || []), []))
  const orphans = ((payload && payload.rows) || [])
    .filter((r) => r && r.sub && !r.cat && !placed.has(r.sub))
    .map((r) => r.sub)
  if (orphans.length) out[UNGROUPED] = Array.from(new Set(orphans)).sort()
  return out
}

/** Per-sub-category counts, for the section's table. */
export function mapSubcategoryRows(payload) {
  return ((payload && payload.rows) || [])
    .filter((r) => r && r.sub)
    .map((r) => ({cat: r.cat || null, sub: r.sub, skus: num(r.skus) || 0}))
}

/** One packing material. `kind` and `branding` do not exist in the master, so they are not invented. */
export function mapPack(p) {
  if (!p || !p.id) return null
  const dims = [num(p.lengthCm), num(p.breadthCm), num(p.heightCm)]
  return {
    id: p.id,
    label: p.label || p.id,
    dims: dims.every((d) => d && d > 0) ? dims : null,
    selfWeightG: num(p.selfWeightG),
    volKg: num(p.volKg),
    status: p.status || null,
    kind: null,
    branding: null,
  }
}

export function mapPacks(rows) {
  return (rows || []).map(mapPack).filter(Boolean)
}
